/**
 * Case notes: the frequency log, the clue board, and the truth surface.
 *
 * The three answer three different questions a player asks at different moments — *where was
 * that?*, *what do I know?*, *how far am I?* — so they are tabs rather than one long column.
 *
 * The design rule throughout: this panel reports, it does not instruct. It never says which clue
 * is missing or which frequency to try next. A mystery that tells you what to do next is a task
 * list; what the player gets is an accurate account of what they have, which is what makes the
 * deduction theirs.
 */
import { h } from '../dom.js';
import { t } from '../../i18n/index.js';
import type { Investigation, InvestigationView, LogEntry } from '../../story/investigation.js';

export type InvestigationTab = 'log' | 'clues' | 'truth';

export interface InvestigationPanelOptions {
  investigation: Investigation;
  /** Tune the receiver to a logged frequency. */
  onTune: (khz: number) => void;
  /**
   * The player worked the notes themselves — changed tab or tuned from a log entry.
   *
   * Distinct from the panel being *shown*, which happens on its own when a clue lands. Chapter
   * 7's operator card uses this, and a card that ticked "read your notes" because the game
   * pushed them at you would be lying.
   */
  onInteract?: () => void;
}

export interface InvestigationPanel {
  element: HTMLElement;
  /** Bring the panel forward on a given tab — used when a clue lands. */
  show(tab?: InvestigationTab): void;
  dispose(): void;
}

export function createInvestigationPanel(options: InvestigationPanelOptions): InvestigationPanel {
  const { investigation, onTune, onInteract } = options;

  let activeTab: InvestigationTab = 'log';
  const panels = new Map<InvestigationTab, HTMLElement>();
  const tabs = new Map<InvestigationTab, HTMLButtonElement>();

  const clueCount = h('span', { class: 'nb-notes__count' });

  const selectTab = (tab: InvestigationTab): void => {
    activeTab = tab;
    for (const [id, button] of tabs) {
      button.setAttribute('aria-selected', String(id === tab));
      button.tabIndex = id === tab ? 0 : -1;
    }
    for (const [id, panel] of panels) panel.hidden = id !== tab;
  };

  const tabStrip = h('div', {
    class: 'nb-notes__tabs',
    role: 'tablist',
    'aria-label': t('investigation.title'),
  });

  for (const tab of ['log', 'clues', 'truth'] as const) {
    const button = h('button', {
      class: 'nb-tab',
      type: 'button',
      role: 'tab',
      id: `nb-notes-tab-${tab}`,
      'aria-controls': `nb-notes-panel-${tab}`,
      'aria-selected': String(tab === activeTab),
      text: t(`investigation.tab.${tab}` as const),
      onclick: () => {
        selectTab(tab);
        onInteract?.();
      },
    }) as HTMLButtonElement;
    tabs.set(tab, button);
    tabStrip.appendChild(button);

    const panel = h('section', {
      class: 'nb-notes__panel',
      id: `nb-notes-panel-${tab}`,
      role: 'tabpanel',
      'aria-labelledby': `nb-notes-tab-${tab}`,
      hidden: tab !== activeTab,
    });
    panels.set(tab, panel);
  }

  // Left/right move between tabs, as a tablist should.
  tabStrip.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const order: InvestigationTab[] = ['log', 'clues', 'truth'];
    const current = order.indexOf(activeTab);
    const next =
      order[(current + (event.key === 'ArrowRight' ? 1 : order.length - 1)) % order.length]!;
    selectTab(next);
    tabs.get(next)?.focus();
    event.preventDefault();
  });

  const element = h(
    'aside',
    { class: 'nb-notes', 'aria-label': t('investigation.title') },
    h(
      'header',
      { class: 'nb-notes__head' },
      h('h2', { class: 'nb-notes__title', text: t('investigation.title') }),
      clueCount,
    ),
    tabStrip,
    ...panels.values(),
  );

  /* ---- renderers ------------------------------------------------------------------------- */

  const renderLog = (view: InvestigationView): HTMLElement[] => {
    if (view.log.length === 0) {
      return [h('p', { class: 'nb-notes__empty', text: t('log.empty') })];
    }

    return [
      h('p', { class: 'nb-notes__hint', text: t('log.heading') }),
      h('ul', { class: 'nb-loglist' }, ...view.log.map((entry) => logRow(entry))),
    ];
  };

  const logRow = (entry: LogEntry): HTMLElement => {
    const note = h('input', {
      class: 'nb-note',
      type: 'text',
      value: entry.note ?? '',
      placeholder: t('log.note.placeholder'),
      'aria-label': t('log.note.label', { khz: entry.khz }),
      maxlength: '120',
    }) as HTMLInputElement;

    // Written on blur rather than on every keystroke: this goes to Postgres, and a note is
    // finished when the player looks away from it.
    note.addEventListener('change', () => {
      if (entry.signalId) investigation.setNote(entry.signalId, note.value.trim());
    });

    return h(
      'li',
      { class: 'nb-logrow' },
      h('button', {
        class: 'nb-logrow__khz',
        type: 'button',
        text: `${entry.khz}`,
        title: t('log.tune', { khz: entry.khz }),
        'aria-label': t('log.tune', { khz: entry.khz }),
        onclick: () => onTune(entry.khz),
      }),
      h(
        'div',
        { class: 'nb-logrow__body' },
        h('span', { class: 'nb-logrow__label', text: entry.label ?? `${entry.khz} kHz` }),
        entry.gameDay !== undefined && entry.gameTime
          ? h('span', {
              class: 'nb-logrow__when',
              text: t('log.entry.time', { day: entry.gameDay, time: entry.gameTime }),
            })
          : null,
        note,
      ),
    );
  };

  const renderClues = (view: InvestigationView): HTMLElement[] => {
    if (view.clues.length === 0) {
      return [h('p', { class: 'nb-notes__empty', text: t('clues.empty') })];
    }
    return [
      h(
        'ul',
        { class: 'nb-clues' },
        ...view.clues.map((clue) =>
          h(
            'li',
            { class: 'nb-clue' },
            h('h3', { class: 'nb-clue__label', text: clue.label }),
            h('p', { class: 'nb-clue__body', text: clue.description }),
          ),
        ),
      ),
    ];
  };

  const renderTruth = (view: InvestigationView): HTMLElement[] => {
    const out: HTMLElement[] = [];
    const premise = investigation.premise();
    if (premise) {
      out.push(
        h('h3', { class: 'nb-notes__subhead', text: t('truth.premise') }),
        h('p', { class: 'nb-notes__premise', text: premise }),
      );
    }

    out.push(h('h3', { class: 'nb-notes__subhead', text: t('truth.threads') }));
    out.push(
      h(
        'ul',
        { class: 'nb-threads' },
        ...view.progress.threads.map((thread) =>
          h(
            'li',
            { class: 'nb-thread', dataset: { complete: String(thread.held === thread.total) } },
            h('span', {
              class: 'nb-thread__label',
              text: t('truth.thread', {
                label: thread.label || t('truth.unthreaded'),
                held: thread.held,
                total: thread.total,
              }),
            }),
            // A bar per thread, so "which part am I short on" is answerable at a glance.
            h(
              'span',
              { class: 'nb-thread__bar', 'aria-hidden': 'true' },
              h('span', {
                class: 'nb-thread__fill',
                style: `width:${thread.total === 0 ? 0 : Math.round((thread.held / thread.total) * 100)}%`,
              }),
            ),
          ),
        ),
      ),
    );

    if (view.ending) {
      out.push(
        h(
          'div',
          { class: 'nb-resolution' },
          h('h3', { class: 'nb-resolution__label', text: t('truth.resolved') }),
          h('p', { class: 'nb-resolution__title', text: view.ending.resolvedLabel }),
          h('p', { class: 'nb-resolution__text', text: view.ending.resolvedText }),
        ),
      );
    } else {
      out.push(h('p', { class: 'nb-notes__empty', text: t('truth.unresolved') }));
    }

    return out;
  };

  const render = (view: InvestigationView): void => {
    clueCount.textContent = t('clues.count', {
      held: view.progress.cluesHeld,
      total: view.progress.cluesTotal,
    });
    panels.get('log')!.replaceChildren(...renderLog(view));
    panels.get('clues')!.replaceChildren(...renderClues(view));
    panels.get('truth')!.replaceChildren(...renderTruth(view));
    element.dataset.resolved = String(Boolean(view.ending));
  };

  const unsubscribe = investigation.view.subscribe(render, true);

  return {
    element,
    show: (tab) => {
      if (tab) selectTab(tab);
      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    },
    dispose: unsubscribe,
  };
}
